import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import Navbar from '../../components/feature/Navbar';

export default function AuthPage() {
  const [activeTab, setActiveTab] = useState<'login' | 'register' | 'forgot' | 'reset-update'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // -------------------------------------------------------------------------
  // Effect: Listen for custom event to switch to login tab
  // -------------------------------------------------------------------------
  useEffect(() => {
    const handleSwitchToLogin = () => {
      setActiveTab('login');
      setError('');
      setSuccess('');
    };

    window.addEventListener('switchToLogin', handleSwitchToLogin);
    return () => window.removeEventListener('switchToLogin', handleSwitchToLogin);
  }, []);

  // -------------------------------------------------------------------------
  // Effect: Detect password‑recovery flow from URL
  // -------------------------------------------------------------------------
  useEffect(() => {
    const type = searchParams.get('type');

    if (type === 'recovery') {
      setActiveTab('reset-update');

      // Verify that the magic‑link session is still valid
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!session) {
          setError('Şifre sıfırlama oturumu geçersiz veya süresi dolmuş. Lütfen tekrar deneyin.');
          setTimeout(() => setActiveTab('forgot'), 3000);
        }
      });
    }
  }, [searchParams]);

  // -------------------------------------------------------------------------
  // Effect: Redirect authenticated users away from the auth page
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (user && activeTab !== 'forgot' && activeTab !== 'reset-update') {
      navigate('/', { replace: true });
    }
  }, [user, navigate, activeTab]);

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------
  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhone(formatPhone(raw));
  };

  // -------------------------------------------------------------------------
  // Form submit handler
  // -------------------------------------------------------------------------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // ---------------------------------------------------------------
    // Forgot‑password flow
    // ---------------------------------------------------------------
    if (activeTab === 'forgot') {
      if (!email.trim()) {
        setError('Lütfen e-posta adresinizi girin.');
        return;
      }

      setLoading(true);
      try {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}${window.location.pathname}?type=recovery`,
        });

        if (resetError) {
          console.error('Reset error:', resetError);
          setError('Şifre sıfırlama bağlantısı gönderilemedi. Lütfen e-posta adresinizi kontrol edin.');
        } else {
          setSuccess('✅ Şifre sıfırlama bağlantısı e-posta adresinize gönderildi. Lütfen gelen kutunuzu kontrol edin.');
          setEmail('');
        }
      } catch (err: any) {
        console.error('Password reset error:', err);
        setError('Bir hata oluştu. Lütfen tekrar deneyin.');
      } finally {
        setLoading(false);
      }
      return;
    }

    // ---------------------------------------------------------------
    // Password‑update (after recovery) flow
    // ---------------------------------------------------------------
    if (activeTab === 'reset-update') {
      if (newPassword.length < 8) {
        setError('Şifre en az 8 karakter olmalıdır.');
        return;
      }
      if (!/[A-Z]/.test(newPassword)) {
        setError('Şifre en az bir büyük harf içermelidir.');
        return;
      }
      if (!/[a-z]/.test(newPassword)) {
        setError('Şifre en az bir küçük harf içermelidir.');
        return;
      }
      if (!/[0-9]/.test(newPassword)) {
        setError('Şifre en az bir rakam içermelidir.');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('Şifreler eşleşmiyor.');
        return;
      }

      setSubmitting(true);
      try {
        const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });

        if (updateError) {
          console.error('Password update error:', updateError);
          setError('Şifre güncellenemedi. Oturum süresi dolmuş olabilir. Lütfen tekrar şifre sıfırlama isteği gönderin.');
        } else {
          setSuccess('✅ Şifreniz başarıyla güncellendi! Giriş sayfasına yönlendiriliyorsunuz...');
          setTimeout(() => {
            setActiveTab('login');
            setNewPassword('');
            setConfirmPassword('');
            setError('');
            setSuccess('');
            // Clean the URL query string
            window.history.replaceState({}, '', window.location.pathname);
          }, 2000);
        }
      } catch (err) {
        console.error('Unexpected error:', err);
        setError('Bir hata oluştu. Lütfen tekrar deneyin.');
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // ---------------------------------------------------------------
    // Login / Register flow
    // ---------------------------------------------------------------
    setSubmitting(true);
    try {
      if (activeTab === 'login') {
        const { error: err } = await signIn(email, password);
        if (err) setError(err);
      } else if (activeTab === 'register') {
        // ---- Validation -------------------------------------------------
        if (!fullName.trim()) {
          setError('Lütfen adınızı girin.');
          return;
        }
        const phoneDigits = phone.replace(/\D/g, '');
        if (phoneDigits.length < 10) {
          setError('Lütfen geçerli bir telefon numarası girin (10 haneli).');
          return;
        }
        if (password.length < 8) {
          setError('Şifre en az 8 karakter olmalıdır.');
          return;
        }
        if (!/[A-Z]/.test(password)) {
          setError('Şifre en az bir büyük harf içermelidir.');
          return;
        }
        if (!/[a-z]/.test(password)) {
          setError('Şifre en az bir küçük harf içermelidir.');
          return;
        }
        if (!/[0-9]/.test(password)) {
          setError('Şifre en az bir rakam içermelidir.');
          return;
        }

        // ---- Registration -----------------------------------------------
        const { error: err } = await signUp(email, password, fullName, phoneDigits);
        if (err) {
          setError(err);
        } else {
          setSuccess('Kayıt başarılı! Lütfen e-posta adresinizi kontrol edin ve hesabınızı doğrulayın.');
        }
      }
    } catch {
      setError('Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // UI helpers
  // -------------------------------------------------------------------------
  const switchTab = (tab: 'login' | 'register' | 'forgot' | 'reset-update') => {
    setActiveTab(tab);
    setError('');
    setSuccess('');
  };

  const getTitle = () => {
    switch (activeTab) {
      case 'login':
        return 'Hesabınıza Giriş Yapın';
      case 'register':
        return 'Yeni Hesap Oluşturun';
      case 'forgot':
        return 'Şifrenizi Sıfırlayın';
      case 'reset-update':
        return 'Yeni Şifre Belirleyin';
    }
  };

  const getSubtitle = () => {
    switch (activeTab) {
      case 'login':
        return 'BAGA ile piyasayı takip etmeye devam edin';
      case 'register':
        return 'Ücretsiz hesap oluşturun ve hemen başlayın';
      case 'forgot':
        return 'E-posta adresinize şifre sıfırlama bağlantısı göndereceğiz';
      case 'reset-update':
        return 'Hesabınız için yeni bir şifre belirleyin';
    }
  };

  const getIcon = () => {
    switch (activeTab) {
      case 'forgot':
        return 'ri-lock-unlock-line';
      case 'reset-update':
        return 'ri-key-2-line';
      default:
        return 'ri-user-line';
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-dark text-beige-light font-sans">
      <Navbar />
      <main className="pt-28 pb-20 px-4">
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald to-emerald-light rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className={`${getIcon()} text-3xl text-dark`}></i>
            </div>
            <h1 className="text-3xl font-bold text-beige-light mb-2">{getTitle()}</h1>
            <p className="text-beige/60 text-sm">{getSubtitle()}</p>
          </div>

          {/* Tab Switcher – only for login / register */}
          {(activeTab === 'login' || activeTab === 'register') && (
            <div className="flex bg-dark-card rounded-full p-1 mb-8 border border-dark-border">
              <button
                onClick={() => switchTab('login')}
                className={`flex-1 py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'login'
                    ? 'bg-emerald text-white shadow-lg shadow-emerald/30'
                    : 'text-beige/60 hover:text-beige'
                }`}
              >
                Giriş Yap
              </button>
              <button
                onClick={() => switchTab('register')}
                className={`flex-1 py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === 'register'
                    ? 'bg-emerald text-white shadow-lg shadow-emerald/30'
                    : 'text-beige/60 hover:text-beige'
                }`}
              >
                Kayıt Ol
              </button>
            </div>
          )}

          {/* Back button for forgot / reset */}
          {(activeTab === 'forgot' || activeTab === 'reset-update') && (
            <button
              onClick={() => switchTab('login')}
              className="flex items-center gap-2 text-beige/60 hover:text-emerald transition-colors text-sm mb-6 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Giriş sayfasına dön
            </button>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} id="auth-form" data-readdy-form className="space-y-5">
            {/* Register – Full Name */}
            {activeTab === 'register' && (
              <div>
                <label className="block text-sm font-medium text-beige/80 mb-2">
                  Ad Soyad <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <i className="ri-user-line absolute left-4 top-1/2 -translate-y-1/2 text-beige/40"></i>
                  <input
                    type="text"
                    name="fullName"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Adınız Soyadınız"
                    className="w-full bg-dark-card border border-dark-border rounded-xl py-3.5 pl-11 pr-4 text-sm text-beige-light placeholder-beige/30 focus:outline-none focus:border-emerald/50 focus:ring-1 focus:ring-emerald/30 transition-all"
                    required
                  />
                </div>
              </div>
            )}

            {/* Register – Phone */}
            {activeTab === 'register' && (
              <div>
                <label className="block text-sm font-medium text-beige/80 mb-2">
                  Telefon Numarası <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <i className="ri-phone-line absolute left-4 top-1/2 -translate-y-1/2 text-beige/40"></i>
                  <span className="absolute left-10 top-1/2 -translate-y-1/2 text-beige/50 text-sm">+90</span>
                  <input
                    type="tel"
                    name="phone"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="(5XX) XXX XXXX"
                    className="w-full bg-dark-card border border-dark-border rounded-xl py-3.5 pl-[4.5rem] pr-4 text-sm text-beige-light placeholder-beige/30 focus:outline-none focus:border-emerald/50 focus:ring-1 focus:ring-emerald/30 transition-all"
                    required
                  />
                </div>
              </div>
            )}

            {/* Email – login / register / forgot */}
            {activeTab !== 'reset-update' && (
              <div>
                <label className="block text-sm font-medium text-beige/80 mb-2">
                  E-posta <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-beige/40"></i>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ornek@email.com"
                    className="w-full bg-dark-card border border-dark-border rounded-xl py-3.5 pl-11 pr-4 text-sm text-beige-light placeholder-beige/30 focus:outline-none focus:border-emerald/50 focus:ring-1 focus:ring-emerald/30 transition-all"
                    required
                  />
                </div>
              </div>
            )}

            {/* Password – login / register */}
            {(activeTab === 'login' || activeTab === 'register') && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-beige/80">
                    Şifre <span className="text-red-400">*</span>
                  </label>
                  {activeTab === 'login' && (
                    <button
                      type="button"
                      onClick={() => switchTab('forgot')}
                      className="text-xs text-emerald hover:text-emerald-light transition-colors cursor-pointer"
                    >
                      Şifremi Unuttum
                    </button>
                  )}
                </div>
                <div className="relative">
                  <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-beige/40"></i>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="En az 8 karakter"
                    className="w-full bg-dark-card border border-dark-border rounded-xl py-3.5 pl-11 pr-12 text-sm text-beige-light placeholder-beige/30 focus:outline-none focus:border-emerald/50 focus:ring-1 focus:ring-emerald/30 transition-all"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-beige/40 hover:text-beige transition-colors cursor-pointer"
                  >
                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                  </button>
                </div>

                {/* Password strength checklist – only for registration */}
                {activeTab === 'register' && (
                  <div className="mt-2.5 space-y-1">
                    <div className="flex items-center gap-2">
                      <i
                        className={`text-xs ${
                          password.length >= 8 ? 'ri-checkbox-circle-fill text-emerald' : 'ri-checkbox-blank-circle-line text-beige/30'
                        }`}
                      ></i>
                      <span className={`text-xs ${password.length >= 8 ? 'text-emerald' : 'text-beige/40'}`}>En az 8 karakter</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i
                        className={`text-xs ${
                          /[A-Z]/.test(password) ? 'ri-checkbox-circle-fill text-emerald' : 'ri-checkbox-blank-circle-line text-beige/30'
                        }`}
                      ></i>
                      <span className={`text-xs ${/[A-Z]/.test(password) ? 'text-emerald' : 'text-beige/40'}`}>En az bir büyük harf (A-Z)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i
                        className={`text-xs ${
                          /[a-z]/.test(password) ? 'ri-checkbox-circle-fill text-emerald' : 'ri-checkbox-blank-circle-line text-beige/30'
                        }`}
                      ></i>
                      <span className={`text-xs ${/[a-z]/.test(password) ? 'text-emerald' : 'text-beige/40'}`}>En az bir küçük harf (a-z)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i
                        className={`text-xs ${
                          /[0-9]/.test(password) ? 'ri-checkbox-circle-fill text-emerald' : 'ri-checkbox-blank-circle-line text-beige/30'
                        }`}
                      ></i>
                      <span className={`text-xs ${/[0-9]/.test(password) ? 'text-emerald' : 'text-beige/40'}`}>En az bir rakam (0-9)</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* New Password – reset‑update */}
            {activeTab === 'reset-update' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-beige/80 mb-2">
                    Yeni Şifre <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-beige/40"></i>
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="En az 8 karakter"
                      className="w-full bg-dark-card border border-dark-border rounded-xl py-3.5 pl-11 pr-12 text-sm text-beige-light placeholder-beige/30 focus:outline-none focus:border-emerald/50 focus:ring-1 focus:ring-emerald/30 transition-all"
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-beige/40 hover:text-beige transition-colors cursor-pointer"
                    >
                      <i className={showNewPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                    </button>
                  </div>

                  {/* Strength checklist */}
                  <div className="mt-2.5 space-y-1">
                    <div className="flex items-center gap-2">
                      <i
                        className={`text-xs ${
                          newPassword.length >= 8 ? 'ri-checkbox-circle-fill text-emerald' : 'ri-checkbox-blank-circle-line text-beige/30'
                        }`}
                      ></i>
                      <span className={`text-xs ${newPassword.length >= 8 ? 'text-emerald' : 'text-beige/40'}`}>En az 8 karakter</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i
                        className={`text-xs ${
                          /[A-Z]/.test(newPassword) ? 'ri-checkbox-circle-fill text-emerald' : 'ri-checkbox-blank-circle-line text-beige/30'
                        }`}
                      ></i>
                      <span className={`text-xs ${/[A-Z]/.test(newPassword) ? 'text-emerald' : 'text-beige/40'}`}>En az bir büyük harf (A-Z)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i
                        className={`text-xs ${
                          /[a-z]/.test(newPassword) ? 'ri-checkbox-circle-fill text-emerald' : 'ri-checkbox-blank-circle-line text-beige/30'
                        }`}
                      ></i>
                      <span className={`text-xs ${/[a-z]/.test(newPassword) ? 'text-emerald' : 'text-beige/40'}`}>En az bir küçük harf (a-z)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i
                        className={`text-xs ${
                          /[0-9]/.test(newPassword) ? 'ri-checkbox-circle-fill text-emerald' : 'ri-checkbox-blank-circle-line text-beige/30'
                        }`}
                      ></i>
                      <span className={`text-xs ${/[0-9]/.test(newPassword) ? 'text-emerald' : 'text-beige/40'}`}>En az bir rakam (0-9)</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-beige/80 mb-2">
                    Yeni Şifre (Tekrar) <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-beige/40"></i>
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Şifrenizi tekrar girin"
                      className="w-full bg-dark-card border border-dark-border rounded-xl py-3.5 pl-11 pr-12 text-sm text-beige-light placeholder-beige/30 focus:outline-none focus:border-emerald/50 focus:ring-1 focus:ring-emerald/30 transition-all"
                      required
                      minLength={8}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Error / Success messages */}
            {error && (
              <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                <i className="ri-error-warning-line text-red-400 text-lg flex-shrink-0"></i>
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {success && (
              <div className="flex items-center gap-3 bg-emerald/10 border border-emerald/20 rounded-xl p-4">
                <i className="ri-check-line text-emerald text-lg flex-shrink-0"></i>
                <p className="text-emerald text-sm">{success}</p>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={submitting || loading}
              className="w-full bg-emerald hover:bg-emerald-light text-white py-4 rounded-xl font-semibold transition-all shadow-lg shadow-emerald/30 hover:shadow-emerald/50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {(submitting || loading) ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  {activeTab === 'forgot' ? 'Gönderiliyor...' : 'İşleniyor...'}
                </span>
              ) : activeTab === 'login' ? (
                'Giriş Yap'
              ) : activeTab === 'register' ? (
                'Hesap Oluştur'
              ) : activeTab === 'forgot' ? (
                'Sıfırlama Bağlantısı Gönder'
              ) : (
                'Şifreyi Güncelle'
              )}
            </button>
          </form>

          {/* Footer text – context‑aware links */}
          <p className="text-center text-beige/40 text-xs mt-8">
            {activeTab === 'login' && (
              <>
                Hesabınız yok mu?{' '}
                <button onClick={() => switchTab('register')} className="text-emerald hover:text-emerald-light cursor-pointer">
                  Kayıt olun
                </button>
              </>
            )}
            {activeTab === 'register' && (
              <>
                Zaten hesabınız var mı?{' '}
                <button onClick={() => switchTab('login')} className="text-emerald hover:text-emerald-light cursor-pointer">
                  Giriş yapın
                </button>
              </>
            )}
            {activeTab === 'forgot' && (
              <>
                Şifrenizi hatırladınız mı?{' '}
                <button onClick={() => switchTab('login')} className="text-emerald hover:text-emerald-light cursor-pointer">
                  Giriş yapın
                </button>
              </>
            )}
          </p>
        </div>
      </main>
    </div>
  );
}
